import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { UserPlus, X, Copy, Check, Share2 } from "lucide-react";

function buildMessage(url) {
  return (
    `🛡️ Je teste Iacover — une app qui lit mes contrats d'assurance, repère les doublons ` +
    `et me dit si je suis bien couvert. Tu veux l'essayer avec moi ?\n\n` +
    `👉 ${url}\n\n` +
    `Deux façons de commencer :\n` +
    `• Crée ton compte en 30 s (des contrats d'exemple sont déjà chargés)\n` +
    `• Ou connecte-toi avec le compte de test : test@iacover.fr / test1234\n\n` +
    `📱 iPhone (Safari) : bouton Partager → « Sur l'écran d'accueil »\n` +
    `🤖 Android (Chrome) : menu ⋮ → « Installer l'application »`
  );
}

export const InviteButton = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const message = buildMessage(url);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Message copié — collez-le où vous voulez !");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Iacover", text: message, url });
        return;
      } catch {
        /* annulé ou non supporté → on retombe sur la copie */
      }
    }
    copy();
  };

  return (
    <>
      <button
        data-testid="invite-open-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 h-9 pl-2.5 pr-3 rounded-xl border border-border hover:bg-muted transition text-sm font-medium"
        title="Inviter un testeur"
      >
        <UserPlus className="w-4 h-4" />
        <span className="hidden sm:inline">Inviter</span>
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="invite-modal">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in-up">
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted transition"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
                <UserPlus className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-xl font-bold tracking-tight mt-4">Inviter un testeur</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Copiez ce message et envoyez-le par SMS, WhatsApp ou email. Tout y est : le lien, le compte de test et comment installer l'app.
              </p>

              <textarea
                data-testid="invite-message"
                readOnly
                value={message}
                rows={9}
                onFocus={(e) => e.target.select()}
                className="mt-4 w-full p-3.5 rounded-xl border border-input bg-muted/40 text-sm outline-none focus:ring-2 focus:ring-ring/40 resize-none leading-relaxed"
              />

              <div className="flex items-center gap-2 mt-4">
                <button
                  data-testid="invite-copy-btn"
                  onClick={copy}
                  className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copié !" : "Copier le message"}
                </button>
                <button
                  data-testid="invite-share-btn"
                  onClick={share}
                  className="h-11 px-4 rounded-xl border border-border font-medium text-sm hover:bg-muted transition flex items-center justify-center gap-2"
                >
                  <Share2 className="w-4 h-4" /> Partager
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default InviteButton;
